import React, { Fragment, useEffect, useState } from "react";
import CheckoutCouponsPickers from "components/CheckoutCouponsPickers";
import CheckoutAttributeToggle from "components/CheckoutAttributeToggle";
import { useFormikContext } from "formik";
import SectionBezel from "components/SectionBezel";
import { getIconForUID } from "components/FormerEditor/utils";
import get from "lodash/get";
import { STATE_KEYS } from "utils/constants/state";
import Toggle from "react-toggle";
import { getToggleProps } from "utils/form";
import { STRIPE_EDUCATION } from "constants/education";
import classnames from "classnames";
import { CHECKOUT_SESSION } from "utils/constants/stripe";
import { getCheckoutAttributesConfig } from "utils/checkout/attributes";
import { useShortLinkDiscounts } from "utils/hooks/shortLink";
import DiscountsSummary from "./DiscountsSummary";
import AllowPromoCodeSummary from "./AllowPromoCodeSummary";

const CouponsFields = ({ name, inherited }) => {
  const [open, setOpen] = useState(false);
  const {
    values,
    touched,
    setFieldTouched,
    setFieldValue
  } = useFormikContext();
  const canEdit = !Boolean(values.id);
  const LockIcon = getIconForUID("lock");
  const locked = !canEdit ? <LockIcon size={18} /> : null;

  const {
    discountsError,
    allowPromotionCodesError,
    allowPromotionCodes,
    activeInheritence,
    inheritedDiscounts,
    inheritedAllowPromotionCodes,
    hasDiscounts,
    discounts,
    hasDefinedInheritedPromotionCodes,
    hasDefinedInheritedDiscounts
  } = useShortLinkDiscounts({ inherited });

  /**
   * Unset discounts when allow promo codes is true and discounts are present
   */
  useEffect(() => {
    if (allowPromotionCodes && discounts && discounts.length) {
      setFieldValue(STATE_KEYS.SHORT_LINK.DISCOUNTS, []);
    }
  }, [allowPromotionCodes]);

  const disableDynamicPromoCodes = !canEdit && hasDiscounts;
  const showCouponPicker =
    (canEdit && !allowPromotionCodes) || disableDynamicPromoCodes;

  const toggleProps = {
    attributes: getCheckoutAttributesConfig({
      name,
      attributes: [
        CHECKOUT_SESSION.ALLOW_PROMOTION_CODES,
        CHECKOUT_SESSION.DISCOUNTS
      ]
    }),
    open,
    onChange: (updatedOpen) => updatedOpen && setOpen(updatedOpen)
  };
  const bezelProps = {
    header: "Discounts",
    expandable: true,
    isOpen: open,
    setOpen,
    action: disableDynamicPromoCodes ? (
      locked
    ) : (
      <CheckoutAttributeToggle {...toggleProps} />
    )
  };

  let content = null;
  if (activeInheritence) {
    content = hasDefinedInheritedDiscounts ? (
      <DiscountsSummary discounts={inheritedDiscounts} error={discountsError} />
    ) : hasDefinedInheritedPromotionCodes ? (
      <AllowPromoCodeSummary
        active={Boolean(inheritedAllowPromotionCodes)}
        error={allowPromotionCodesError}
      />
    ) : null;
  } else {
    content = (
      <Fragment>
        <div
          className={classnames("flex flex-row justify-between items-center", {
            "mb3 pb3 bb hairline-1": showCouponPicker
          })}
        >
          <div className="flex flex-column w-100">
            <div className="flex flex-row items-center justify-between">
              <label
                className={classnames("f5 fw6 db", {
                  "color-error": allowPromotionCodesError
                })}
              >
                {STRIPE_EDUCATION.ALLOW_PROMOTION_CODES.title}
              </label>
              <Toggle
                {...getToggleProps({
                  active: allowPromotionCodes,
                  /**
                   * Disable the toggle when a created shortLink has discounts
                   */
                  disabled: disableDynamicPromoCodes,
                  setFieldValue: (_, active) => {
                    if (
                      !get(touched, STATE_KEYS.SHORT_LINK.ALLOW_PROMOTION_CODES)
                    ) {
                      setFieldTouched(
                        STATE_KEYS.SHORT_LINK.ALLOW_PROMOTION_CODES,
                        true
                      );
                    }

                    setTimeout(
                      () =>
                        setFieldValue(
                          STATE_KEYS.SHORT_LINK.ALLOW_PROMOTION_CODES,
                          active
                        ),
                      0
                    );
                  }
                })}
              />
            </div>
            <div className="f6 pt2 lh-copy">
              {disableDynamicPromoCodes
                ? STRIPE_EDUCATION.ALLOW_PROMOTION_CODES.disabled
                : STRIPE_EDUCATION.ALLOW_PROMOTION_CODES.checkout}
            </div>
          </div>
        </div>
        {showCouponPicker && (
          <Fragment>
            <label
              className={classnames("f5 fw6 db pb2", {
                "color-error": discountsError
              })}
            >
              Coupons
            </label>
            <CheckoutCouponsPickers
              rootPath="data.product"
              name="checkout.discounts"
              editMode={canEdit}
            />
          </Fragment>
        )}
      </Fragment>
    );
  }

  return <SectionBezel {...bezelProps}>{content}</SectionBezel>;
};

export default CouponsFields;
